import {
  putUsersPreferencesUnits,
  useGetUsersPreferencesUnitsSuspense,
} from "@imi/api-client/index.js";
import {
  buildUnitsPreferences,
  UnitsPreferences,
  unitsPreferencesSchema,
  UnitType,
} from "@src/core/api/unitsPreferences";
import { unitsRepository } from "@src/core/engine/units/unitsRepository";
import { useToast } from "@src/shared/components/Toast/useToast";
import { useAppForm } from "@src/shared/helpers/form";
import { isNotNil } from "@src/shared/helpers/guards";
import { logger } from "@src/shared/helpers/logger";
import { mergeWithDefaults } from "@src/shared/helpers/mergeWithDefaults";
import { parseApiError } from "@src/shared/helpers/parseApiError";
import { setUnchangedToNull } from "@src/shared/helpers/setUnchangedToNull";
import { revalidateLogic } from "@tanstack/react-form";
import { mapEntries, tryit } from "radashi";
import { useMemo, useState } from "react";

type UseUnitsFormOptions = {
  onSubmit?: () => void;
};

export function useUnitsForm(options: UseUnitsFormOptions = {}) {
  const { addToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const { data } = useGetUsersPreferencesUnitsSuspense();
  const unitsPreferences = useMemo(() => buildUnitsPreferences(data), [data]);
  const defaultValue: UnitsPreferences = mapEntries(
    unitsRepository.getOptions(),
    (key, value) => [key, value.default],
  );

  const form = useAppForm({
    defaultValues: mergeWithDefaults(unitsPreferences, defaultValue),
    async onSubmit({ value }) {
      setSubmitting(true);
      const changedValues = setUnchangedToNull(value, defaultValue);
      const body = Object.entries(changedValues)
        .filter(([, value]) => isNotNil(value))
        .reduce<{ type: UnitType; value: string }[]>(
          (acc, [key, value]) => [
            ...acc,
            {
              type: key as UnitType,
              value: value as string,
            },
          ],
          [],
        );

      const [error] = await tryit(putUsersPreferencesUnits)({ body });
      setSubmitting(false);

      if (error) {
        const apiError = parseApiError({ error });
        logger.error("Failed to update units preference", {
          apiError,
          error,
        });
        return;
      }

      addToast({
        duration: 3000,
        message: "Your default units have been updated.",
        title: "Settings saved successfully",
      });
      options?.onSubmit?.();
    },
    validationLogic: revalidateLogic({
      mode: "submit",
      modeAfterSubmission: "change",
    }),
    validators: { onDynamic: unitsPreferencesSchema },
  });

  return { defaultValue, form, submitting };
}
